/*
  ЦИФРОВОЙ ДИАК ЗФ — PROJECT STORE
  Central state management with localStorage persistence.
  All project data is editable and persisted across sessions.
*/

import {
  PROJECTS,
  GANTT_ITEMS,
  type ProjectPassport,
  type ProjectTask,
  type GanttItem,
} from '@/data/projects';

// ─── Roadmap phases (динамические этапы дорожной карты) ─────────────────────

export interface RoadmapPhase {
  id: string;
  order: number;
  label: string;
  period: string;
  title?: string;
  color: string;
}

/** Стабильные id для миграции со старых `phase: 1|2|3`. */
export const LEGACY_PHASE_TO_ID: Record<1 | 2 | 3, string> = {
  1: 'roadmap-phase-1',
  2: 'roadmap-phase-2',
  3: 'roadmap-phase-3',
};

export const DEFAULT_ROADMAP_PHASES: RoadmapPhase[] = [
  {
    id: 'roadmap-phase-1',
    order: 1,
    label: 'Этап 1',
    period: 'Q2 2026',
    title: 'Завершение пилотов и подготовка к масштабированию',
    color: '#00A651',
  },
  {
    id: 'roadmap-phase-2',
    order: 2,
    label: 'Этап 2',
    period: 'Q2–Q4 2026',
    title: 'Ключевые процессы: ОВСС и Ремонты',
    color: '#0055A5',
  },
  {
    id: 'roadmap-phase-3',
    order: 3,
    label: 'Этап 3',
    period: 'Q4 2026 – Q2 2027',
    title: 'Ключевые процессы: КС и ИТ, поддерживающие процессы',
    color: '#4A90D9',
  },
];

export function normalizeRoadmapPhases(raw: unknown): RoadmapPhase[] {
  if (!Array.isArray(raw) || raw.length === 0) {
    return DEFAULT_ROADMAP_PHASES.map((p) => ({ ...p }));
  }
  const out: RoadmapPhase[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const o = item as Record<string, unknown>;
    if (typeof o.id !== 'string' || typeof o.order !== 'number') continue;
    out.push({
      id: o.id,
      order: o.order,
      label: typeof o.label === 'string' ? o.label : 'Этап',
      period: typeof o.period === 'string' ? o.period : '',
      title: typeof o.title === 'string' ? o.title : undefined,
      color: typeof o.color === 'string' ? o.color : '#0055A5',
    });
  }
  if (out.length === 0) return DEFAULT_ROADMAP_PHASES.map((p) => ({ ...p }));
  return out.sort((a, b) => a.order - b.order);
}

export function roadmapPhasesSorted(phases: RoadmapPhase[]): RoadmapPhase[] {
  return [...phases].sort((a, b) => a.order - b.order);
}

export function phaseLabelFromMeta(phase: RoadmapPhase): string {
  return `${phase.label} — ${phase.period}`;
}

/** Индекс этапа в отсортированном списке (для сортировки сайдбара). */
export function phaseOrderIndex(phaseId: string, phases: RoadmapPhase[]): number {
  const sorted = roadmapPhasesSorted(phases);
  const idx = sorted.findIndex((p) => p.id === phaseId);
  return idx >= 0 ? idx : 999;
}

// ─── Extended types for full editability ──────────────────────────────────

export type RiskLevel = 'high' | 'medium' | 'low';
export type RiskProbability = 'high' | 'medium' | 'low';
export type RiskStatus = 'open' | 'mitigated' | 'accepted' | 'closed';

export interface AgileRisk {
  id: string;
  title: string;
  description: string;
  probability: RiskProbability;
  impact: RiskLevel;
  status: RiskStatus;
  owner: string;
  mitigation: string;
  contingency: string;
  createdAt: string;
  updatedAt: string;
}

export interface KPI {
  id: string;
  label: string;
  value: string;
  unit: string;
  trend?: 'up' | 'down' | 'stable';
}

export interface Stakeholder {
  id: string;
  name: string;
  role: string;
  involvement: 'responsible' | 'accountable' | 'consulted' | 'informed';
}

export type BacklogItemPriority = 'critical' | 'high' | 'medium' | 'low';

/**
 * Теги канбан-карточки. Пока зарезервировано одно значение — 'bug'.
 * Расширять список — в client/src/lib/backlogTags.ts (одна запись в BACKLOG_TAGS).
 */
export type BacklogItemTag = 'bug';

/**
 * Метаданные вложения карточки. Бинарник живёт в data/project-files/<projectId>/<id>,
 * а на карточке храним только идентификатор и человеко-читаемую обёртку.
 */
export interface BacklogAttachment {
  id: string;
  name: string;
  mime: string;
  size: number;
  uploadedAt: string;
  uploadedById?: number;
  uploadedByLogin?: string;
}

/**
 * Комментарий к карточке канбана. Хранится внутри BacklogItem — переживает
 * сохранение проекта в рабочем пространстве. Редактирование после создания
 * не реализовано: комментарии можно только добавлять и удалять (автор/админ).
 */
export interface BacklogComment {
  id: string;
  authorLogin: string;
  authorId?: number;
  body: string;
  createdAt: string;
  /** ISO-строка момента последнего редактирования. Не задано — комментарий не правили. */
  editedAt?: string;
}

/**
 * Статус подзадачи. Намеренно фиксированный набор (в отличие от настраиваемых
 * колонок канбана у родителя) — чтобы прогресс подзадач был сравним между
 * карточками и проектами. Соответствует дефолтным колонкам канбана.
 */
export type BacklogSubtaskStatus = 'backlog' | 'todo' | 'in_progress' | 'review' | 'done';

export const BACKLOG_SUBTASK_STATUSES: readonly BacklogSubtaskStatus[] = [
  'backlog',
  'todo',
  'in_progress',
  'review',
  'done',
];

/**
 * Подзадача (чек-листовый sub-item) внутри карточки канбана. Имеет фиксированный
 * статус (Бэклог / Сделать / В работе / На оценке / Сделано), опциональный
 * дедлайн и опционального исполнителя. Сохраняется в составе BacklogItem.subtasks.
 *
 * Поле `done` оставлено для обратной совместимости со старыми данными:
 * мы пишем оба поля синхронно (`done = status === 'done'`), а при чтении
 * используем `status`, если он есть, иначе выводим из `done`.
 */
/**
 * Вложение к подзадаче. Бинарник живёт там же, где вложения карточки
 * (data/project-files/<projectId>/<id>) — переиспользуется тот же endpoint.
 * Поле `kind` фиксирует, был ли файл загружен как финальный артефакт «Сделано»
 * (final) или стал промежуточным после отката подзадачи в работу (intermediate).
 */
export interface SubtaskAttachment extends BacklogAttachment {
  kind: 'final' | 'intermediate';
}

export interface BacklogSubtask {
  id: string;
  title: string;
  /** Текущий статус подзадачи; для старых данных выводится из `done`. */
  status: BacklogSubtaskStatus;
  /** Дублёр статуса для backward-compat: `done = status === 'done'`. */
  done: boolean;
  /** Опциональный дедлайн подзадачи в формате ISO YYYY-MM-DD. */
  dueDate?: string | null;
  /** Опциональный исполнитель — ФИО/логин (если отличается от родителя). */
  assignee?: string;
  /** Порядок внутри списка подзадач (0 = первая сверху). */
  order: number;
  createdAt: string;
  /** ISO момента, когда отметили выполненной; null/undefined — ещё не выполнялась. */
  completedAt?: string | null;
  /** Вложения подзадачи: файлы-артефакты. Доступно только когда `status === 'done'`. */
  attachments?: SubtaskAttachment[];
}

/** Сторона ответственности по карточке (для двухнедельного отчёта). */
export type BacklogItemSide = 'customer' | 'office';

export interface BacklogItem {
  id: string;
  title: string;
  description: string;
  priority: BacklogItemPriority;
  /** Идентификатор колонки канбана (раньше — жёсткий union-статус). */
  status: string;
  assignee: string;
  storyPoints: number;
  sprint?: string;
  /** Дата старта работы над карточкой в формате ISO YYYY-MM-DD (день, без времени). */
  startDate?: string | null;
  /** Дедлайн карточки в формате ISO YYYY-MM-DD (день, без времени). */
  dueDate?: string | null;
  /** Теги карточки (например, «Баг»). */
  tags?: BacklogItemTag[];
  /** Вложения (скриншоты, файлы — хранятся в /api/project-files/<projectId>/<id>). */
  attachments?: BacklogAttachment[];
  /** Комментарии к карточке (хронологический порядок — от старых к новым). */
  comments?: BacklogComment[];
  /** Подзадачи карточки (чек-лист, отсортирован по order, выполненные не скрываются). */
  subtasks?: BacklogSubtask[];
  /**
   * Сторона ответственности: 'customer' (ДИАК ЗФ заказчик) или 'office'
   * (проектный офис, исполнители). Если не задано — считается 'office'.
   * Используется в двухнедельной отчётности для разделения «что сделано
   * со стороны заказчика» vs «что сделано проектным офисом».
   */
  side?: BacklogItemSide;
  /**
   * Эскалация: текст «нужно от руководства» — что заблокировано или
   * требует решения на верхнем уровне. Если задано — карточка попадает
   * в раздел «Эскалации» отчётности.
   */
  escalation?: string;
  /**
   * Идентификатор связанной задачи Ганта. Для `ganttTaskSource === 'plan'` —
   * это id из `ganttTasks[]` проекта. Для `'fact'` — id задачи из рабочей
   * Google-таблицы (факт-Гант), где id берётся из 1-й колонки.
   */
  ganttTaskId?: string;
  /**
   * Источник связанной задачи: 'plan' (план-график в дашборде, по умолчанию)
   * или 'fact' (факт-Гант в Google-таблице проекта).
   */
  ganttTaskSource?: 'plan' | 'fact';
  /** Позиция карточки внутри своей колонки. */
  kanbanOrder: number;
  createdAt: string;
  updatedAt: string;
}

/** Колонка канбан-доски проекта. Набор настраивается пользователем. */
export interface KanbanColumn {
  id: string;
  label: string;
  order: number;
}

/** Дефолтные колонки для новых проектов и миграции существующих. */
export const DEFAULT_KANBAN_COLUMNS: readonly KanbanColumn[] = [
  { id: 'backlog', label: 'Бэклог', order: 0 },
  { id: 'todo', label: 'Сделать', order: 1 },
  { id: 'in_progress', label: 'В работе', order: 2 },
  { id: 'review', label: 'На оценке', order: 3 },
  { id: 'done', label: 'Сделано', order: 4 },
];

export function defaultKanbanColumns(): KanbanColumn[] {
  return DEFAULT_KANBAN_COLUMNS.map((c) => ({ ...c }));
}

function isKanbanColumnLike(v: unknown): v is Partial<KanbanColumn> {
  return !!v && typeof v === 'object';
}

export function normalizeKanbanColumns(raw: unknown): KanbanColumn[] {
  if (!Array.isArray(raw) || raw.length === 0) return defaultKanbanColumns();
  const seen = new Set<string>();
  const out: KanbanColumn[] = [];
  for (const item of raw) {
    if (!isKanbanColumnLike(item)) continue;
    const id = typeof item.id === 'string' && item.id.trim() ? item.id.trim() : '';
    if (!id || seen.has(id)) continue;
    const label = typeof item.label === 'string' && item.label.trim() ? item.label.trim() : id;
    const order = typeof item.order === 'number' && Number.isFinite(item.order)
      ? item.order
      : out.length;
    seen.add(id);
    out.push({ id, label, order });
  }
  if (out.length === 0) return defaultKanbanColumns();
  out.sort((a, b) => a.order - b.order);
  return out.map((c, i) => ({ ...c, order: i }));
}

function coercePriority(raw: unknown): BacklogItemPriority {
  if (raw === 'critical' || raw === 'high' || raw === 'medium' || raw === 'low') return raw;
  return 'medium';
}

/**
 * Привести список задач к колонкам: задачам с неизвестным `status` ставим id первой колонки,
 * приоритет нормализуем, обеспечиваем числовой `kanbanOrder` (0..n−1 по текущему порядку в колонке).
 */
export function normalizeBacklogForColumns(
  raw: unknown,
  columns: KanbanColumn[],
): BacklogItem[] {
  if (!Array.isArray(raw) || columns.length === 0) return [];
  const colIds = new Set(columns.map((c) => c.id));
  const fallback = columns[0]!.id;
  const items: BacklogItem[] = [];
  const isoDay = (v: unknown): string | null => {
    if (typeof v !== 'string') return null;
    const t = v.trim();
    if (!t) return null;
    // Принимаем YYYY-MM-DD и ISO-datetime, приводим к первым 10 символам.
    const m = /^(\d{4}-\d{2}-\d{2})/.exec(t);
    return m ? m[1] : null;
  };
  const KNOWN_TAGS: ReadonlySet<BacklogItemTag> = new Set<BacklogItemTag>(['bug']);
  const readTags = (v: unknown): BacklogItemTag[] | undefined => {
    if (!Array.isArray(v)) return undefined;
    const out: BacklogItemTag[] = [];
    const seen = new Set<string>();
    for (const t of v) {
      if (typeof t !== 'string') continue;
      if (!KNOWN_TAGS.has(t as BacklogItemTag)) continue;
      if (seen.has(t)) continue;
      seen.add(t);
      out.push(t as BacklogItemTag);
    }
    return out.length > 0 ? out : undefined;
  };
  const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const readComments = (v: unknown): BacklogComment[] | undefined => {
    if (!Array.isArray(v)) return undefined;
    const out: BacklogComment[] = [];
    for (const raw of v) {
      if (!raw || typeof raw !== 'object') continue;
      const o = raw as Record<string, unknown>;
      const id = typeof o.id === 'string' && o.id ? o.id : null;
      const authorLogin = typeof o.authorLogin === 'string' && o.authorLogin ? o.authorLogin : null;
      const body = typeof o.body === 'string' ? o.body.trim() : '';
      if (!id || !authorLogin || !body) continue;
      const createdAt =
        typeof o.createdAt === 'string' && o.createdAt ? o.createdAt : new Date().toISOString();
      const authorId =
        typeof o.authorId === 'number' && Number.isFinite(o.authorId) ? o.authorId : undefined;
      const editedAt =
        typeof o.editedAt === 'string' && o.editedAt ? o.editedAt : undefined;
      out.push({ id, authorLogin, authorId, body: body.slice(0, 100000), createdAt, editedAt });
    }
    if (out.length === 0) return undefined;
    out.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
    return out;
  };
  const KNOWN_SUBTASK_STATUSES: ReadonlySet<BacklogSubtaskStatus> = new Set<BacklogSubtaskStatus>([
    'backlog',
    'todo',
    'in_progress',
    'review',
    'done',
  ]);
  const readSubtasks = (v: unknown): BacklogSubtask[] | undefined => {
    if (!Array.isArray(v)) return undefined;
    const out: BacklogSubtask[] = [];
    const seen = new Set<string>();
    for (const raw of v) {
      if (!raw || typeof raw !== 'object') continue;
      const o = raw as Record<string, unknown>;
      const id = typeof o.id === 'string' && o.id ? o.id : null;
      if (!id || seen.has(id)) continue;
      const title = typeof o.title === 'string' ? o.title.trim() : '';
      if (!title) continue;
      // Сначала пытаемся прочитать новый `status`, иначе мигрируем из `done`.
      let status: BacklogSubtaskStatus;
      if (typeof o.status === 'string' && KNOWN_SUBTASK_STATUSES.has(o.status as BacklogSubtaskStatus)) {
        status = o.status as BacklogSubtaskStatus;
      } else if (o.done === true) {
        status = 'done';
      } else {
        status = 'todo';
      }
      const done = status === 'done';
      const dueDate = isoDay(o.dueDate);
      const assignee =
        typeof o.assignee === 'string' && o.assignee.trim() ? o.assignee.trim() : undefined;
      const order =
        typeof o.order === 'number' && Number.isFinite(o.order) ? o.order : Number.NaN;
      const createdAt =
        typeof o.createdAt === 'string' && o.createdAt ? o.createdAt : new Date().toISOString();
      const completedAt =
        typeof o.completedAt === 'string' && o.completedAt ? o.completedAt : null;
      const rawAtts = Array.isArray(o.attachments) ? (o.attachments as unknown[]) : [];
      const subAtts: SubtaskAttachment[] = [];
      for (const ra of rawAtts) {
        if (!ra || typeof ra !== 'object') continue;
        const ao = ra as Record<string, unknown>;
        if (typeof ao.id !== 'string' || !UUID_RE.test(ao.id)) continue;
        if (typeof ao.name !== 'string' || !ao.name) continue;
        if (typeof ao.mime !== 'string' || !ao.mime) continue;
        const size = typeof ao.size === 'number' && Number.isFinite(ao.size) ? ao.size : 0;
        const uploadedAt =
          typeof ao.uploadedAt === 'string' && ao.uploadedAt
            ? ao.uploadedAt
            : new Date().toISOString();
        const uploadedById =
          typeof ao.uploadedById === 'number' && Number.isFinite(ao.uploadedById)
            ? ao.uploadedById
            : undefined;
        const uploadedByLogin =
          typeof ao.uploadedByLogin === 'string' && ao.uploadedByLogin ? ao.uploadedByLogin : undefined;
        const kind: 'final' | 'intermediate' =
          ao.kind === 'intermediate' ? 'intermediate' : 'final';
        subAtts.push({
          id: ao.id,
          name: fixMojibakeName(ao.name).slice(0, 240),
          mime: ao.mime,
          size,
          uploadedAt,
          uploadedById,
          uploadedByLogin,
          kind,
        });
      }
      seen.add(id);
      out.push({
        id,
        title: title.slice(0, 500),
        status,
        done,
        dueDate,
        assignee,
        order,
        createdAt,
        completedAt: done ? completedAt : null,
        attachments: subAtts.length > 0 ? subAtts : undefined,
      });
    }
    if (out.length === 0) return undefined;
    out.sort((a, b) => {
      const ao = Number.isFinite(a.order) ? a.order : Number.POSITIVE_INFINITY;
      const bo = Number.isFinite(b.order) ? b.order : Number.POSITIVE_INFINITY;
      if (ao !== bo) return ao - bo;
      return a.createdAt.localeCompare(b.createdAt);
    });
    return out.map((s, i) => ({ ...s, order: i }));
  };
  const readAttachments = (v: unknown): BacklogAttachment[] | undefined => {
    if (!Array.isArray(v)) return undefined;
    const out: BacklogAttachment[] = [];
    for (const raw of v) {
      if (!raw || typeof raw !== 'object') continue;
      const o = raw as Record<string, unknown>;
      if (typeof o.id !== 'string' || !UUID_RE.test(o.id)) continue;
      if (typeof o.name !== 'string' || !o.name) continue;
      if (typeof o.mime !== 'string' || !o.mime) continue;
      const size = typeof o.size === 'number' && Number.isFinite(o.size) ? o.size : 0;
      const uploadedAt =
        typeof o.uploadedAt === 'string' && o.uploadedAt
          ? o.uploadedAt
          : new Date().toISOString();
      const uploadedById =
        typeof o.uploadedById === 'number' && Number.isFinite(o.uploadedById)
          ? o.uploadedById
          : undefined;
      const uploadedByLogin =
        typeof o.uploadedByLogin === 'string' && o.uploadedByLogin
          ? o.uploadedByLogin
          : undefined;
      out.push({
        id: o.id,
        name: fixMojibakeName(o.name).slice(0, 240),
        mime: o.mime,
        size,
        uploadedAt,
        uploadedById,
        uploadedByLogin,
      });
    }
    return out.length > 0 ? out : undefined;
  };
  for (const it of raw) {
    if (!it || typeof it !== 'object') continue;
    const o = it as Record<string, unknown>;
    if (typeof o.id !== 'string' || typeof o.title !== 'string') continue;
    const status = typeof o.status === 'string' && colIds.has(o.status) ? o.status : fallback;
    const now = new Date().toISOString();
    items.push({
      id: o.id,
      title: o.title,
      description: typeof o.description === 'string' ? o.description : '',
      priority: coercePriority(o.priority),
      status,
      assignee: typeof o.assignee === 'string' ? o.assignee : '',
      storyPoints:
        typeof o.storyPoints === 'number' && Number.isFinite(o.storyPoints) ? o.storyPoints : 0,
      sprint: typeof o.sprint === 'string' && o.sprint ? o.sprint : undefined,
      startDate: isoDay(o.startDate),
      dueDate: isoDay(o.dueDate),
      tags: readTags(o.tags),
      attachments: readAttachments(o.attachments),
      comments: readComments(o.comments),
      subtasks: readSubtasks(o.subtasks),
      side:
        o.side === 'customer' || o.side === 'office'
          ? (o.side as BacklogItemSide)
          : undefined,
      escalation:
        typeof o.escalation === 'string' && o.escalation.trim() ? o.escalation : undefined,
      ganttTaskId:
        typeof o.ganttTaskId === 'string' && o.ganttTaskId ? o.ganttTaskId : undefined,
      ganttTaskSource:
        o.ganttTaskSource === 'fact' ? 'fact' : o.ganttTaskSource === 'plan' ? 'plan' : undefined,
      kanbanOrder:
        typeof o.kanbanOrder === 'number' && Number.isFinite(o.kanbanOrder)
          ? o.kanbanOrder
          : Number.NaN,
      createdAt: typeof o.createdAt === 'string' ? o.createdAt : now,
      updatedAt: typeof o.updatedAt === 'string' ? o.updatedAt : now,
    });
  }
  // Пересчёт kanbanOrder внутри каждой колонки: сохраняем ранее заданный порядок (если был),
  // новые/NaN — в конец в том порядке, в котором встретились.
  const byCol = new Map<string, BacklogItem[]>();
  for (const col of columns) byCol.set(col.id, []);
  for (const it of items) {
    byCol.get(it.status)!.push(it);
  }
  const result: BacklogItem[] = [];
  for (const col of columns) {
    const list = byCol.get(col.id)!;
    list.sort((a, b) => {
      const ao = Number.isFinite(a.kanbanOrder) ? a.kanbanOrder : Number.POSITIVE_INFINITY;
      const bo = Number.isFinite(b.kanbanOrder) ? b.kanbanOrder : Number.POSITIVE_INFINITY;
      if (ao !== bo) return ao - bo;
      return a.createdAt.localeCompare(b.createdAt);
    });
    list.forEach((it, i) => {
      result.push({ ...it, kanbanOrder: i });
    });
  }
  return result;
}

/** Статус строки план-графика на странице проекта. `behind` — «В работе», но дата окончания уже в прошлом (авто). */
export type GanttTaskPlanStatus = 'active' | 'planned' | 'done' | 'behind';

/** Статус паспорта проекта в воркспейсе (значение «pilot» в JSON приводится к active). */
export type ProjectPassportStatus = 'active' | 'planned' | 'done';

export function coerceProjectPassportStatus(raw: unknown): ProjectPassportStatus {
  if (raw === 'active' || raw === 'planned' || raw === 'done') return raw;
  if (raw === 'pilot') return 'active';
  return 'planned';
}

export interface EditableGanttTask {
  id: string;
  title: string;
  start: string;
  end: string;
  status: GanttTaskPlanStatus;
  /** Список исполнителей (ФИО, e-mail/login или подразделение). */
  assignees: string[];
  description: string;
  progress: number;
  /** Порядок строк в таблице план-графика (0 = первая сверху). Сохраняется в workspace. */
  planOrder?: number;
}

/**
 * Извлечь массив исполнителей из произвольной записи задачи (поддерживает legacy-форму `assignee: string`).
 * Разделяет строки через запятую и убирает пустые значения, сохраняя порядок.
 */
export function readAssigneesFromRaw(raw: unknown): string[] {
  if (!raw || typeof raw !== 'object') return [];
  const r = raw as Record<string, unknown>;
  const out: string[] = [];
  if (Array.isArray(r.assignees)) {
    for (const v of r.assignees) {
      if (typeof v === 'string') {
        for (const part of v.split(',')) {
          const t = part.trim();
          if (t) out.push(t);
        }
      }
    }
  }
  if (out.length === 0 && typeof r.assignee === 'string') {
    for (const part of r.assignee.split(',')) {
      const t = part.trim();
      if (t) out.push(t);
    }
  }
  const seen = new Set<string>();
  const dedup: string[] = [];
  for (const s of out) {
    const k = s.trim().toLowerCase().replace(/\s+/g, ' ');
    if (seen.has(k)) continue;
    seen.add(k);
    dedup.push(s);
  }
  return dedup;
}

/** Сортировка «как раньше», если у задач ещё нет planOrder (миграция). */
function legacyGanttRowSort(a: EditableGanttTask, b: EditableGanttTask): number {
  const c = a.start.localeCompare(b.start);
  if (c !== 0) return c;
  const t = a.title.localeCompare(b.title, 'ru');
  if (t !== 0) return t;
  return a.id.localeCompare(b.id);
}

/** Порядок отображения строк ганта проекта. */
export function compareGanttTasksDisplayOrder(a: EditableGanttTask, b: EditableGanttTask): number {
  const ao = a.planOrder;
  const bo = b.planOrder;
  if (typeof ao === 'number' && typeof bo === 'number' && ao !== bo) return ao - bo;
  if (typeof ao === 'number' && typeof bo !== 'number') return -1;
  if (typeof ao !== 'number' && typeof bo === 'number') return 1;
  return legacyGanttRowSort(a, b);
}

/** При отсутствии planOrder выставить 0..n−1 по старой сортировке (дата → название → id). */
export function ensureGanttTaskPlanOrders(tasks: EditableGanttTask[]): EditableGanttTask[] {
  if (tasks.length === 0) return tasks;
  const missing = tasks.some(
    (t) => typeof t.planOrder !== 'number' || !Number.isFinite(t.planOrder),
  );
  if (!missing) return tasks;
  const sorted = [...tasks].sort(legacyGanttRowSort);
  return sorted.map((t, i) => ({ ...t, planOrder: i }));
}

/** Элемент списка «Содержание проекта» на странице проекта. */
export interface ProjectScopeLine {
  text: string;
  done?: boolean;
}

/** Одна загруженная версия файла (хранится на диске сервера, id = имя файла). */
export interface ProjectDocumentVersion {
  id: string;
  originalName: string;
  mime: string;
  size: number;
  uploadedAt: string;
  uploadedById: number;
  uploadedByLogin: string;
}

/**
 * Группа документов («дорожка документооборота»): слева направо «вход → по ходу
 * → итог» для проекта или логические разделы для глобального архива.
 */
export interface DocumentGroup {
  id: string;
  label: string;
  order: number;
  /**
   * Если true — «системная» группа из шаблона по умолчанию (нельзя удалить, но
   * можно переименовать). Это защита от случайного стирания базовых разделов.
   */
  system?: boolean;
}

/** Бейдж-тип документа (ТЗ, Регламент, Отчёт и т.п.). Редактируется из UI. */
export interface DocumentType {
  id: string;
  label: string;
  /** HEX цвета для пилюли-бейджа. Если пусто — берётся серый по умолчанию. */
  color?: string;
}

/** Конфиг документооборота: набор групп + набор типов. Один формат для проектов и глобального архива. */
export interface DocumentsConfig {
  groups: DocumentGroup[];
  types: DocumentType[];
}

/** Логический документ: название, группа (дорожка), тип-бейдж, цепочка версий. */
export interface ProjectDocument {
  id: string;
  title: string;
  /**
   * Идентификатор группы (дорожки) из `DocumentsConfig.groups`. Для старых
   * документов, где хранилось поле `category`, подставляется при нормализации.
   */
  groupId: string;
  /** Идентификатор типа-бейджа из `DocumentsConfig.types`. `null` — без типа. */
  typeId: string | null;
  /**
   * Устаревшее поле: свободный текстовый идентификатор категории. Оставляем для
   * совместимости со старыми клиентами на переходный период — новая версия его
   * больше не использует, при сохранении оно просто «едет» как есть.
   */
  category?: string;
  versions: ProjectDocumentVersion[];
  /** Если задан — документ привязан к строке план-графика (Ганта) на странице проекта. */
  sourceGanttTaskId?: string;
}

// Идентификаторы дефолтных групп/типов проекта. Вынесены как константы,
// чтобы миграция старых данных и сид дефолтного конфига использовали один и тот же набор.
export const DOC_GROUP_INCOMING = 'incoming';
export const DOC_GROUP_WORKING = 'working';
export const DOC_GROUP_OUTGOING = 'outgoing';

export const DOC_TYPE_TZ = 'tz';
export const DOC_TYPE_CONTRACT = 'contract';
export const DOC_TYPE_PROTOCOL = 'protocol';
export const DOC_TYPE_REPORT = 'report';
export const DOC_TYPE_ACT = 'act';
export const DOC_TYPE_PRESENTATION = 'presentation';
export const DOC_TYPE_SPEC = 'spec';
export const DOC_TYPE_GANTT_DELIVERABLE = 'gantt_deliverable';
export const DOC_TYPE_OTHER = 'other';

/** Палитра для бейджей-типов (HEX). Используется и в редакторе структуры. */
export const DOC_TYPE_COLOR_PALETTE: string[] = [
  '#0055A5', // синий
  '#00A651', // зелёный
  '#E85D04', // оранжевый
  '#D0021B', // красный
  '#7C3AED', // фиолетовый
  '#0EA5E9', // голубой
  '#F5A623', // жёлтый
  '#64748B', // серый
];

/** Дефолтный конфиг документов ДЛЯ ПРОЕКТА: три дорожки «вход → работа → итог». */
export function defaultProjectDocumentsConfig(): DocumentsConfig {
  return {
    groups: [
      { id: DOC_GROUP_INCOMING, label: 'Входящие', order: 0 },
      { id: DOC_GROUP_WORKING, label: 'Рабочие артефакты', order: 1 },
      { id: DOC_GROUP_OUTGOING, label: 'Итоговые', order: 2 },
    ],
    types: [
      { id: DOC_TYPE_TZ, label: 'ТЗ', color: '#0055A5' },
      { id: DOC_TYPE_CONTRACT, label: 'Контракт/Приказ', color: '#7C3AED' },
      { id: DOC_TYPE_PROTOCOL, label: 'Протокол', color: '#0EA5E9' },
      { id: DOC_TYPE_SPEC, label: 'Спецификация', color: '#64748B' },
      { id: DOC_TYPE_GANTT_DELIVERABLE, label: 'Результат задачи плана', color: '#F5A623' },
      { id: DOC_TYPE_REPORT, label: 'Отчёт', color: '#00A651' },
      { id: DOC_TYPE_ACT, label: 'Акт', color: '#D0021B' },
      { id: DOC_TYPE_PRESENTATION, label: 'Презентация', color: '#E85D04' },
      { id: DOC_TYPE_OTHER, label: 'Прочее' },
    ],
  };
}

// Дефолтные группы глобального архива (под общим Гантом).
export const GLOBAL_DOC_GROUP_REGULATIONS = 'regulations';
export const GLOBAL_DOC_GROUP_METHODOLOGY = 'methodology';
export const GLOBAL_DOC_GROUP_TEMPLATES = 'templates';
export const GLOBAL_DOC_GROUP_PRESENTATIONS = 'presentations';
export const GLOBAL_DOC_GROUP_PROTOCOLS = 'protocols';
export const GLOBAL_DOC_GROUP_OTHER = 'other';

/** Дефолтный конфиг глобального архива «Документы ДИАКа» на главной. */
export function defaultGlobalDocumentsConfig(): DocumentsConfig {
  return {
    groups: [
      { id: GLOBAL_DOC_GROUP_REGULATIONS, label: 'Регламенты', order: 0 },
      { id: GLOBAL_DOC_GROUP_METHODOLOGY, label: 'Методологии', order: 1 },
      { id: GLOBAL_DOC_GROUP_TEMPLATES, label: 'Шаблоны', order: 2 },
      { id: GLOBAL_DOC_GROUP_PRESENTATIONS, label: 'Презентации', order: 3 },
      { id: GLOBAL_DOC_GROUP_PROTOCOLS, label: 'Протоколы и решения', order: 4 },
      { id: GLOBAL_DOC_GROUP_OTHER, label: 'Прочее', order: 5 },
    ],
    types: [
      { id: 'regulation', label: 'Регламент', color: '#0055A5' },
      { id: 'methodology', label: 'Методология', color: '#7C3AED' },
      { id: 'template', label: 'Шаблон', color: '#00A651' },
      { id: 'presentation', label: 'Презентация', color: '#E85D04' },
      { id: 'protocol', label: 'Протокол', color: '#0EA5E9' },
      { id: 'order', label: 'Приказ', color: '#D0021B' },
      { id: DOC_TYPE_OTHER, label: 'Прочее' },
    ],
  };
}

/**
 * Глобальный архив «Документы ДИАКа»: хранится в отдельном workspace-документе
 * (`doc_key = "global_documents"`), структура параллельна проекту.
 */
export interface GlobalDocumentsStore {
  config: DocumentsConfig;
  documents: ProjectDocument[];
}

/** Псевдо-идентификатор проекта для файлов глобального архива (на диске и в URL). */
export const GLOBAL_SCOPE_PROJECT_ID = '__global__';

export function asScopeLine(raw: unknown): ProjectScopeLine {
  if (typeof raw === 'string') return { text: raw, done: false };
  if (raw && typeof raw === 'object' && 'text' in raw && typeof (raw as ProjectScopeLine).text === 'string') {
    const o = raw as ProjectScopeLine;
    return { text: o.text, done: Boolean(o.done) };
  }
  return { text: '', done: false };
}

export function normalizeProjectScope(scope: unknown): ProjectScopeLine[] {
  if (!Array.isArray(scope)) return [];
  return scope.map(asScopeLine);
}

export interface EditableProject {
  id: string;
  title: string;
  shortTitle: string;
  icon: string;
  status: ProjectPassportStatus;
  aiReadiness: 'high' | 'medium' | 'needs_analysis';
  aiPotential: string;
  group: 'core' | 'support';
  /** Id этапа из `roadmapPhases` (workspace). */
  phaseId: string;
  phaseLabel: string;
  description: string;
  goal: string;
  scope: ProjectScopeLine[];
  risks: AgileRisk[];
  kpis: KPI[];
  stakeholders: Stakeholder[];
  tags: string[];
  repoUrl: string;
  /**
   * Ссылка на рабочую Google-таблицу с фактическим план-графиком (Гант «факт»).
   * Встраивается во вьювер под планом. Таблица должна быть «опубликована в
   * интернете» или открыта «всем, у кого есть ссылка», иначе iframe её не покажет.
   */
  factGanttUrl?: string;
  startDate: string;
  targetDate: string;
  progress: number;
  ganttTasks: EditableGanttTask[];
  backlog: BacklogItem[];
  /** Колонки канбан-доски проекта (набор и порядок). Если отсутствует — берутся дефолтные. */
  kanbanColumns: KanbanColumn[];
  /** Вложения: версии файлов, метаданные в workspace JSON. */
  documents: ProjectDocument[];
  /**
   * Конфиг документооборота именно этого проекта (группы-дорожки + типы-бейджи).
   * Если отсутствует — используется дефолт `defaultProjectDocumentsConfig()`.
   * Редактируется прямо из UI («Настроить структуру»).
   */
  documentsConfig?: DocumentsConfig;
  /** Порядок карточки среди core-проектов в том же этапе дорожной карты (для DnD на главной). */
  orderInPhase?: number;
  /**
   * Дата фактической активации проекта (ISO YYYY-MM-DD), выставляется
   * автоматически в момент перевода `status` в `'active'`. Используется на
   * общем ганте: если `activatedAt < startDate` соответствующей полосы —
   * слева от колбаски рисуется индикатор «старт с опережением графика».
   * Не сбрасывается при дальнейших переключениях статуса — это исторический
   * факт.
   */
  activatedAt?: string;
  updatedAt: string;
}

/**
 * Лечит имена файлов, у которых UTF-8 байты были декодированы как Latin-1.
 * Исторически в workspace-документах сохранялись имена вида «Ð¢ÐµÑÑ.docx»,
 * потому что multer на сервере декодировал multipart filename как latin1.
 * На сервере это уже поправлено при загрузке/отдаче, а здесь — ретроактивно
 * исправляем уже сохранённые значения при рендере (безопасно и без миграций).
 */
export function fixMojibakeName(s: string): string {
  if (!/[\u00C0-\u00FF]/.test(s)) return s;
  try {
    const bytes = new Uint8Array(s.length);
    for (let i = 0; i < s.length; i++) {
      const code = s.charCodeAt(i);
      if (code > 0xff) return s;
      bytes[i] = code;
    }
    const decoded = new TextDecoder('utf-8', { fatal: false }).decode(bytes);
    if (decoded.includes('\uFFFD')) return s;
    if (!/[\u0400-\u04FF\u0100-\u017F]/.test(decoded)) return s;
    return decoded;
  } catch {
    return s;
  }
}

function normalizeDocumentVersion(raw: unknown): ProjectDocumentVersion | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  if (typeof o.id !== 'string' || typeof o.originalName !== 'string') return null;
  return {
    id: o.id,
    originalName: fixMojibakeName(o.originalName),
    mime: typeof o.mime === 'string' ? o.mime : 'application/octet-stream',
    size: typeof o.size === 'number' && Number.isFinite(o.size) ? o.size : 0,
    uploadedAt: typeof o.uploadedAt === 'string' ? o.uploadedAt : new Date().toISOString(),
    uploadedById: typeof o.uploadedById === 'number' ? o.uploadedById : 0,
    uploadedByLogin: typeof o.uploadedByLogin === 'string' ? o.uploadedByLogin : '',
  };
}

/**
 * Маппинг устаревшего поля `category` (плоская категория) → (groupId, typeId)
 * нового конфига для проектов. Применяется один раз при нормализации.
 */
function migrateLegacyCategory(category: string): { groupId: string; typeId: string | null } {
  switch (category) {
    case 'tz':
      return { groupId: DOC_GROUP_INCOMING, typeId: DOC_TYPE_TZ };
    case 'contracts':
      return { groupId: DOC_GROUP_INCOMING, typeId: DOC_TYPE_CONTRACT };
    case 'reports':
      return { groupId: DOC_GROUP_OUTGOING, typeId: DOC_TYPE_REPORT };
    case 'gantt_deliverable':
      return { groupId: DOC_GROUP_WORKING, typeId: DOC_TYPE_GANTT_DELIVERABLE };
    case 'other':
    case '':
    default:
      return { groupId: DOC_GROUP_WORKING, typeId: null };
  }
}

/**
 * Нормализация `DocumentsConfig` из workspace JSON: защищаемся от мусора,
 * проставляем `order`, оставляем `system`-флаг только у известных id. Если
 * передан пустой/кривой raw — возвращаем undefined, вызывающий решает, брать
 * дефолт или нет.
 */
export function normalizeDocumentsConfig(raw: unknown): DocumentsConfig | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const o = raw as Record<string, unknown>;
  const rawGroups = Array.isArray(o.groups) ? o.groups : [];
  const rawTypes = Array.isArray(o.types) ? o.types : [];
  const groups: DocumentGroup[] = [];
  const seenGroupIds = new Set<string>();
  rawGroups.forEach((g, idx) => {
    if (!g || typeof g !== 'object') return;
    const gg = g as Record<string, unknown>;
    const id = typeof gg.id === 'string' ? gg.id.trim() : '';
    if (!id || seenGroupIds.has(id)) return;
    seenGroupIds.add(id);
    const label = typeof gg.label === 'string' && gg.label.trim() ? gg.label.trim() : id;
    const order = typeof gg.order === 'number' && Number.isFinite(gg.order) ? gg.order : idx;
    const system = gg.system === true ? true : undefined;
    groups.push(system ? { id, label, order, system } : { id, label, order });
  });
  groups.sort((a, b) => a.order - b.order);
  groups.forEach((g, i) => {
    g.order = i;
  });
  const types: DocumentType[] = [];
  const seenTypeIds = new Set<string>();
  for (const t of rawTypes) {
    if (!t || typeof t !== 'object') continue;
    const tt = t as Record<string, unknown>;
    const id = typeof tt.id === 'string' ? tt.id.trim() : '';
    if (!id || seenTypeIds.has(id)) continue;
    seenTypeIds.add(id);
    const label = typeof tt.label === 'string' && tt.label.trim() ? tt.label.trim() : id;
    const color = typeof tt.color === 'string' && /^#[0-9a-fA-F]{3,8}$/.test(tt.color) ? tt.color : undefined;
    types.push(color ? { id, label, color } : { id, label });
  }
  if (groups.length === 0 && types.length === 0) return undefined;
  return { groups, types };
}

/**
 * Убедиться, что `documentsConfig` содержит все группы/типы, на которые
 * ссылаются документы. Если нет — добавить недостающие как «(внешний)» элементы,
 * чтобы документ не «потерялся» после ручной правки конфига.
 */
function ensureConfigCoversDocuments(
  config: DocumentsConfig,
  docs: ProjectDocument[],
): DocumentsConfig {
  const groupIds = new Set(config.groups.map((g) => g.id));
  const typeIds = new Set(config.types.map((t) => t.id));
  const groups = [...config.groups];
  const types = [...config.types];
  for (const d of docs) {
    if (d.groupId && !groupIds.has(d.groupId)) {
      groupIds.add(d.groupId);
      groups.push({ id: d.groupId, label: 'Прочее', order: groups.length });
    }
    if (d.typeId && !typeIds.has(d.typeId)) {
      typeIds.add(d.typeId);
      types.push({ id: d.typeId, label: d.typeId });
    }
  }
  return { groups, types };
}

export function getProjectDocumentsConfig(p: EditableProject): DocumentsConfig {
  const base = p.documentsConfig ?? defaultProjectDocumentsConfig();
  return ensureConfigCoversDocuments(base, p.documents);
}

function normalizeProjectDocuments(raw: unknown): ProjectDocument[] {
  if (!Array.isArray(raw)) return [];
  const out: ProjectDocument[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const o = item as Record<string, unknown>;
    if (typeof o.id !== 'string') continue;
    const versionsRaw = Array.isArray(o.versions) ? o.versions : [];
    const versions = versionsRaw.map(normalizeDocumentVersion).filter(Boolean) as ProjectDocumentVersion[];
    versions.sort((a, b) => new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime());
    const sourceGanttTaskId =
      typeof o.sourceGanttTaskId === 'string' && o.sourceGanttTaskId.trim()
        ? o.sourceGanttTaskId.trim()
        : undefined;
    const legacyCategory = typeof o.category === 'string' ? o.category : '';
    // Новые поля имеют приоритет; если их нет — используем миграцию из category.
    const groupIdRaw = typeof o.groupId === 'string' && o.groupId.trim() ? o.groupId.trim() : '';
    const typeIdRaw =
      typeof o.typeId === 'string' && o.typeId.trim() ? o.typeId.trim() : null;
    let groupId = groupIdRaw;
    let typeId: string | null = typeIdRaw;
    if (!groupId) {
      const m = migrateLegacyCategory(legacyCategory);
      groupId = m.groupId;
      if (typeId === null) typeId = m.typeId;
    }
    out.push({
      id: o.id,
      title: typeof o.title === 'string' ? o.title : 'Документ',
      groupId,
      typeId,
      // Сохраняем старое поле как есть — для совместимости со старым клиентом.
      ...(legacyCategory ? { category: legacyCategory } : {}),
      versions,
      ...(sourceGanttTaskId ? { sourceGanttTaskId } : {}),
    });
  }
  return out;
}

/** Нормализация всего `GlobalDocumentsStore` (конфиг + документы). */
export function normalizeGlobalDocumentsStore(raw: unknown): GlobalDocumentsStore {
  const fallback: GlobalDocumentsStore = {
    config: defaultGlobalDocumentsConfig(),
    documents: [],
  };
  if (!raw || typeof raw !== 'object') return fallback;
  const o = raw as Record<string, unknown>;
  const cfg = normalizeDocumentsConfig(o.config) ?? defaultGlobalDocumentsConfig();
  const docs = normalizeProjectDocuments(o.documents).map((d) => {
    // Для глобального архива дефолтная группа — «Прочее», если ссылка на неизвестную.
    if (!cfg.groups.some((g) => g.id === d.groupId)) {
      return { ...d, groupId: GLOBAL_DOC_GROUP_OTHER };
    }
    return d;
  });
  return { config: ensureConfigCoversDocuments(cfg, docs), documents: docs };
}

function migratePhaseIdFromUnknown(
  raw: Record<string, unknown>,
  phases: RoadmapPhase[],
): { phaseId: string; phaseLabel: string } {
  let phaseId = typeof raw.phaseId === 'string' && raw.phaseId.trim() ? raw.phaseId.trim() : '';
  if (!phaseId) {
    const legacy = raw.phase;
    const n = legacy === 2 ? 2 : legacy === 3 ? 3 : 1;
    phaseId = phases.find((x) => x.order === n)?.id ?? LEGACY_PHASE_TO_ID[n];
  }
  if (!phases.some((x) => x.id === phaseId)) {
    phaseId = phases[0]?.id ?? LEGACY_PHASE_TO_ID[1];
  }
  const meta = phases.find((x) => x.id === phaseId);
  let phaseLabel = typeof raw.phaseLabel === 'string' ? raw.phaseLabel : '';
  if (!phaseLabel && meta) phaseLabel = phaseLabelFromMeta(meta);
  return { phaseId, phaseLabel };
}

function coerceGanttTaskPlanStatus(raw: unknown): GanttTaskPlanStatus {
  if (raw === 'active' || raw === 'planned' || raw === 'done' || raw === 'behind') return raw;
  if (raw === 'pilot') return 'active';
  return 'planned';
}

function sliceIsoDate(s: string): string | null {
  const t = s.trim().slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(t) ? t : null;
}

function calendarTodayIso(): string {
  const n = new Date();
  const y = n.getFullYear();
  const m = String(n.getMonth() + 1).padStart(2, '0');
  const d = String(n.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * «В работе» + дата окончания раньше сегодня → `behind`.
 * Если срок продлили — `behind` снова становится `active`.
 */
export function syncGanttTaskPlanStatusWithToday(tasks: EditableGanttTask[]): EditableGanttTask[] {
  const today = calendarTodayIso();
  return tasks.map((t) => {
    const end = sliceIsoDate(t.end);
    if (!end) return t;
    if (t.status === 'active' && today > end) {
      return { ...t, status: 'behind' as const };
    }
    if (t.status === 'behind' && today <= end) {
      return { ...t, status: 'active' as const };
    }
    return t;
  });
}

export function normalizeEditableProject(
  p: EditableProject,
  phases: RoadmapPhase[] = DEFAULT_ROADMAP_PHASES,
): EditableProject {
  const raw = p as unknown as Record<string, unknown>;
  const { phaseId, phaseLabel } = migratePhaseIdFromUnknown(raw, phases);
  const rest = { ...p } as Record<string, unknown>;
  delete rest.phase;
  const base = rest as unknown as EditableProject;
  const ganttTasksRaw = Array.isArray(base.ganttTasks)
    ? base.ganttTasks.map((t) => {
        const copy = {
          ...(t as EditableGanttTask & { assignee?: string; dependencies?: unknown }),
        };
        const assignees = readAssigneesFromRaw(t);
        delete (copy as { assignee?: string }).assignee;
        delete (copy as { dependencies?: unknown }).dependencies;
        return {
          ...copy,
          assignees,
          status: coerceGanttTaskPlanStatus(t.status),
        } as EditableGanttTask;
      })
    : [];
  const ganttTasks = syncGanttTaskPlanStatusWithToday(ensureGanttTaskPlanOrders(ganttTasksRaw));
  const kanbanColumns = normalizeKanbanColumns((base as { kanbanColumns?: unknown }).kanbanColumns);
  const backlog = normalizeBacklogForColumns(
    (base as { backlog?: unknown }).backlog,
    kanbanColumns,
  );
  return {
    ...base,
    phaseId,
    phaseLabel,
    status: coerceProjectPassportStatus(base.status),
    factGanttUrl:
      typeof (base as { factGanttUrl?: unknown }).factGanttUrl === 'string'
        ? (base as { factGanttUrl: string }).factGanttUrl
        : undefined,
    ganttTasks,
    // Пересчитываем прогресс на загрузке: старые проекты могли хранить 0%
    // потому что ручной `progress` у задач не заполнялся, а статусы стояли.
    progress: computeProjectProgress(ganttTasks),
    kanbanColumns,
    backlog,
    scope: normalizeProjectScope(p.scope as unknown),
    documents: normalizeProjectDocuments((p as EditableProject).documents as unknown),
    documentsConfig: normalizeDocumentsConfig((p as { documentsConfig?: unknown }).documentsConfig),
    activatedAt: normalizeActivatedAt(
      (raw as { activatedAt?: unknown }).activatedAt,
      coerceProjectPassportStatus(base.status),
    ),
  };
}

/**
 * Принимает «сырой» activatedAt из сохранённого workspace и нормализует к
 * YYYY-MM-DD. Если значение отсутствует, но проект уже в статусе `active`,
 * проставлять дату активации НЕ будем (мы не знаем, когда он реально был
 * активирован) — индикатор раннего старта просто не появится для таких
 * исторических проектов.
 */
function normalizeActivatedAt(
  raw: unknown,
  _status: ProjectPassportStatus,
): string | undefined {
  void _status;
  if (typeof raw !== 'string') return undefined;
  const t = raw.trim();
  if (!t) return undefined;
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(t);
  return m ? m[1] : undefined;
}

// ─── Progress from project plan (Gantt) ───────────────────────────────────

/** Сортировка core-проектов внутри одного этапа: явный orderInPhase, иначе по shortTitle. */
export function compareCoreProjectsInPhase(a: EditableProject, b: EditableProject): number {
  const ao = a.orderInPhase;
  const bo = b.orderInPhase;
  if (ao != null && bo != null && ao !== bo) return ao - bo;
  if (ao != null && bo == null) return -1;
  if (ao == null && bo != null) return 1;
  return a.shortTitle.localeCompare(b.shortTitle, 'ru');
}

/** Список профильных задач для сайдбара: порядок этапа, затем orderInPhase, затем название. */
export function compareSidebarCoreProjects(
  a: EditableProject,
  b: EditableProject,
  phases: RoadmapPhase[] = DEFAULT_ROADMAP_PHASES,
): number {
  const oa = phaseOrderIndex(a.phaseId, phases);
  const ob = phaseOrderIndex(b.phaseId, phases);
  if (oa !== ob) return oa - ob;
  return compareCoreProjectsInPhase(a, b);
}

/** Среднее по полям «% выполнения» задач плана-графика на странице проекта; без задач — 0. */
/**
 * Среднее по задачам Ганта. На вход — массив задач план-графика.
 * Если у задачи явно задан `progress > 0`, используем его (ручной ввод).
 * Иначе — выводим из статуса задачи:
 *   • done    → 100 (задача завершена)
 *   • active  → 50  (в работе)
 *   • behind  → 50  (сдвиг сроков, но работа идёт)
 *   • planned → 0   (ещё не начато)
 * Статус `done` всегда даёт 100, даже если manual progress меньше —
 * «выполнено» как финальный статус сильнее любой ручной цифры.
 */
function statusBasedProgress(status: GanttTaskPlanStatus): number {
  switch (status) {
    case 'done':
      return 100;
    case 'active':
    case 'behind':
      return 50;
    case 'planned':
    default:
      return 0;
  }
}

export function computeProjectProgress(ganttTasks: EditableGanttTask[]): number {
  if (ganttTasks.length === 0) return 0;
  const sum = ganttTasks.reduce((acc, t) => {
    if (t.status === 'done') return acc + 100;
    const manual = Math.min(100, Math.max(0, Number.isFinite(t.progress) ? t.progress : 0));
    if (manual > 0) return acc + manual;
    return acc + statusBasedProgress(t.status);
  }, 0);
  return Math.round(sum / ganttTasks.length);
}

// ─── Conversion helpers ────────────────────────────────────────────────────

function toAgileRisk(r: ProjectPassport['risks'][0], idx: number): AgileRisk {
  return {
    id: `risk-${idx}`,
    title: r.title,
    description: r.title,
    probability: r.level === 'high' ? 'medium' : 'low',
    impact: r.level,
    status: 'open',
    owner: 'ДИАК ЗФ',
    mitigation: r.mitigation,
    contingency: '',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

function toKPI(k: ProjectPassport['kpis'][0], idx: number): KPI {
  return {
    id: `kpi-${idx}`,
    label: k.label,
    value: k.value,
    unit: k.unit ?? '',
    trend: 'stable',
  };
}

function toStakeholder(s: string, idx: number): Stakeholder {
  const roles: Stakeholder['involvement'][] = ['responsible', 'accountable', 'consulted', 'informed'];
  return {
    id: `sh-${idx}`,
    name: s,
    role: '',
    involvement: roles[Math.min(idx, roles.length - 1)],
  };
}

function toGanttTask(t: ProjectTask, planOrder: number): EditableGanttTask {
  const st = t.status;
  const status: GanttTaskPlanStatus =
    st === 'pilot' ? 'active' : st === 'active' || st === 'planned' || st === 'done' ? st : 'planned';
  const progress = st === 'done' ? 100 : st === 'active' || st === 'pilot' ? 50 : 0;
  const assignees = t.assignee
    ? t.assignee
        .split(',')
        .map((x) => x.trim())
        .filter(Boolean)
    : [];
  return {
    id: t.id,
    title: t.title,
    start: t.start,
    end: t.end,
    status,
    assignees,
    description: t.description ?? '',
    progress,
    planOrder,
  };
}

export function passportToEditable(p: ProjectPassport): EditableProject {
  const ganttTasks = (p.monthlyPlan ?? []).map((t, i) => toGanttTask(t, i));
  const n = p.phase === 2 ? 2 : p.phase === 3 ? 3 : 1;
  return {
    id: p.id,
    title: p.title,
    shortTitle: p.shortTitle,
    icon: p.icon,
    status: coerceProjectPassportStatus(p.status),
    aiReadiness: p.aiReadiness,
    aiPotential: p.aiPotential,
    group: p.group,
    phaseId: LEGACY_PHASE_TO_ID[n],
    phaseLabel: p.phaseLabel,
    description: p.description,
    goal: p.goal,
    scope: p.scope.map((text) => ({ text })),
    risks: p.risks.map(toAgileRisk),
    kpis: p.kpis.map(toKPI),
    stakeholders: p.stakeholders.map(toStakeholder),
    tags: [...p.tags],
    repoUrl: p.repoUrl ?? '',
    startDate: p.startDate,
    targetDate: p.targetDate,
    progress: computeProjectProgress(ganttTasks),
    ganttTasks,
    backlog: [],
    kanbanColumns: defaultKanbanColumns(),
    documents: [],
    updatedAt: new Date().toISOString(),
  };
}

// ─── Storage key ──────────────────────────────────────────────────────────

const STORAGE_KEY = 'diak_projects_v2';
const GANTT_STORAGE_KEY = 'diak_gantt_v2';

/** Убрать устаревший g4 и заменить g3, если в storage ещё раздробленный roadmap (digital-roadmap). */
function migrateLegacyGanttShape(items: GanttItem[]): { items: GanttItem[]; changed: boolean } {
  const seedG3 = GANTT_ITEMS.find((g) => g.id === 'g3');
  if (!seedG3) return { items, changed: false };

  let changed = false;
  const withoutG4 = items.filter((g) => {
    if (g.id === 'g4') {
      changed = true;
      return false;
    }
    return true;
  });

  const idx = withoutG4.findIndex((g) => g.id === 'g3');
  if (idx < 0) {
    return { items: withoutG4, changed };
  }

  const g3 = withoutG4[idx]!;
  const outdated =
    g3.projectId === 'digital-roadmap' ||
    g3.projectId !== seedG3.projectId ||
    g3.title !== seedG3.title;

  if (!outdated) {
    return { items: withoutG4, changed };
  }

  const next = [...withoutG4];
  next[idx] = { ...seedG3 };
  return { items: next, changed: true };
}


// ─── Load / Save ──────────────────────────────────────────────────────────

export function loadProjects(): EditableProject[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as EditableProject[];
      // Merge: add any new projects from source that aren't in storage
      const existingIds = new Set(parsed.map(p => p.id));
      const newProjects = PROJECTS.filter(p => !existingIds.has(p.id)).map(passportToEditable);
      return [...parsed, ...newProjects].map((proj) => normalizeEditableProject(proj as EditableProject));
    }
  } catch {
    // fall through to default
  }
  return PROJECTS.map(passportToEditable).map((p) => normalizeEditableProject(p));
}

export function saveProjects(projects: EditableProject[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(projects));
}

export function normalizeGanttItem(
  g: GanttItem,
  phases: RoadmapPhase[] = DEFAULT_ROADMAP_PHASES,
): GanttItem {
  const raw = g as unknown as Record<string, unknown>;
  let phaseId = typeof raw.phaseId === 'string' && raw.phaseId ? raw.phaseId : '';
  if (!phaseId) {
    const ph = raw.phase === 2 ? 2 : raw.phase === 3 ? 3 : 1;
    phaseId = phases.find((x) => x.order === ph)?.id ?? LEGACY_PHASE_TO_ID[ph as 1 | 2 | 3];
  }
  if (!phases.some((x) => x.id === phaseId)) {
    phaseId = phases[0]?.id ?? LEGACY_PHASE_TO_ID[1];
  }
  return {
    id: g.id,
    projectId: g.projectId,
    title: g.title,
    start: g.start,
    end: g.end,
    status: g.status,
    phaseId,
    factStart: typeof raw.factStart === 'string' && raw.factStart ? raw.factStart : undefined,
    factEnd: typeof raw.factEnd === 'string' && raw.factEnd ? raw.factEnd : undefined,
  };
}

export function loadGanttItems(): GanttItem[] {
  try {
    const raw = localStorage.getItem(GANTT_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as GanttItem[];
      const { items, changed } = migrateLegacyGanttShape(parsed);
      if (changed) {
        localStorage.setItem(GANTT_STORAGE_KEY, JSON.stringify(items));
      }
      return items.map((it) => normalizeGanttItem(it));
    }
  } catch { /* fall through */ }
  return GANTT_ITEMS.map((it) => normalizeGanttItem(it));
}

export function saveGanttItems(items: GanttItem[]): void {
  localStorage.setItem(GANTT_STORAGE_KEY, JSON.stringify(items));
}

// ─── Utility ──────────────────────────────────────────────────────────────

export function generateId(prefix = 'item'): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

export function riskScore(r: AgileRisk): number {
  const map = { high: 3, medium: 2, low: 1 };
  return map[r.probability] * map[r.impact];
}

export function riskColor(r: AgileRisk): string {
  const score = riskScore(r);
  if (score >= 6) return '#D0021B';
  if (score >= 3) return '#F5A623';
  return '#00A651';
}

export function riskLabel(r: AgileRisk): string {
  const score = riskScore(r);
  if (score >= 6) return 'Критический';
  if (score >= 3) return 'Умеренный';
  return 'Низкий';
}
